import {observer} from 'mobx-react';
import React from 'react';
import {X8Block, X8BlockType} from '../../../../domain';
import {ReactNodeGenerator} from '../../../../lib/helpers';
import {X9ContentCreate} from '../../x9/X9ContentCreate';
import {X9ContentEdit} from '../../x9/X9ContentEdit';
import {X9ViewComponent} from '../../x9/X9ViewComponent';
import {X7PushableBlockStore} from '../X7PushableBlockStore';
import {DeleteContentControls} from './DeleteContentControls';
import {ReorderContentsControls} from './ReorderContentsControls';
import {X8BlockCardHeader} from './X8BlockCardHeader';
import {userStatusService} from '../../../../domain'

export type BlockMode = 'view-with-actions' | 'view-inactive' |
  'add-content' | 'edit-content' | 'reorder-contents' | 'delete-content';

export type X8BlockComponentProps = {
  mode: BlockMode
  block: X8Block,
  store: X7PushableBlockStore,
  headerContent?: (() => React.ReactNode)
}

function cardHeaderClassName(blockType: X8BlockType): string {
  switch (blockType) {
    case 'Visible':
      return 'card-header alert-primary';
    case 'Pushable':
      return 'card-header alert-success';
    case 'Hidden':
      return 'card-header alert-warning';
  }
}

function* itemsNoActions(block: X8Block,
                         showPlaceholder: boolean = true): ReactNodeGenerator {
  const items = block.Items!;

  if (items.length === 0 && showPlaceholder) {
    yield (
      <div className="row justify-content-center" key="no-items">
        <div className="col-auto">...</div>
      </div>
    );
  }

  if (items.length === 0)
    return;
  for (let item of items) {
    yield (
      <div className="row" key={'content-' + item.Id}>
        <div className="col">
          <X9ViewComponent x9={item}/>
        </div>
      </div>
    );
  }
}

function* itemsWithActions(store: X7PushableBlockStore,
                           block: X8Block): ReactNodeGenerator {
  const items = block.Items!;
  const blockId = block.Id;

  if (items.length === 0) {
    yield (
      <div className="row justify-content-center" key="no-items">
        <div className="col-auto">...</div>
      </div>
    );
    return;
  }

  for (let item of items) {
    yield (
      <div className="row" key={'content-' + item.Id}>
        <div className="col-auto ml-auto content-actions">
          {
            userStatusService.status.roles.ContentManager &&
            <>
            <button type="button" className="btn btn-primary"
                  onClick={() => store.beginEditContent(item)}>
              Edit
            </button>
            <button type="button" className="btn btn-danger"
                    onClick={() => store.beginDeleteContent(blockId, item.Id)}>
              Delete
            </button>
            </>
          }
          
        </div>
        <div className="col-12 videoPreview">
          <X9ViewComponent x9={item}/>
        </div>
      </div>
    );
  }
}

function* addContentItems(store: X7PushableBlockStore,
                          block: X8Block): ReactNodeGenerator {
  const add = store.addContent!;

  yield* itemsNoActions(block, false);

  yield (<X9ContentCreate key="add-content" store={add}/>);
}

function* editContentItems(store: X7PushableBlockStore,
                           block: X8Block): ReactNodeGenerator {
  const items = block.Items!;
  const edit = store.editContent!;
  const editContentId = edit.content.Id;

  for (let item of items) {
    if (item.Id !== editContentId) {
      yield (
        <div className="row" key={'content-' + item.Id}>
          <div className="col">
            <X9ViewComponent x9={item}/>
          </div>
        </div>
      );
      continue;
    }

    yield (<X9ContentEdit key="content-edit" store={edit}/>);
  }
}

function* deleteContentItems(coordinator: X7PushableBlockStore,
                             block: X8Block): ReactNodeGenerator {
  const deleteContentId = coordinator.deleteContent!.contentId;
  const items = block.Items!;

  for (let item of items) {
    yield (
      <div className="row" key={'content-' + item.Id}>
        <div className="col">
          <X9ViewComponent x9={item}/>
        </div>
      </div>
    );

    if (item.Id === deleteContentId)
      yield (<DeleteContentControls key="delete-content-controls"
                                    store={coordinator}/>);
  }
}

function* reorderContentItems(coordinator: X7PushableBlockStore): ReactNodeGenerator {
  const loader = coordinator.loader;
  const reorderContents = coordinator.reorderContents!;
  const itemIds = reorderContents.contentIds;
  const itemsById = loader.contentById;

  yield (<ReorderContentsControls key="reorder-contents-controls"
                                  store={coordinator}/>);

  for (let index = 0; index < itemIds.length; index++) {
    const first = index === 0;
    const last = index === itemIds.length - 1;
    const itemId = itemIds[index];
    const item = itemsById[itemId];

    yield (
      <div className="row" key={'content-' + item.Id}>
        <div className="col">
          <X9ViewComponent x9={item}/>
        </div>
        <div className="col-auto">
          <button type="button" className="btn btn-primary"
                  onClick={() => coordinator.reorderContentUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-primary"
                  onClick={() => coordinator.reorderContentDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>
    );
  }
}

function* renderCardBody(mode: BlockMode,
                         block: X8Block,
                         store: X7PushableBlockStore): ReactNodeGenerator {
  switch (mode) {
    case 'view-with-actions':
      yield* itemsWithActions(store, block);
      return;
    case 'view-inactive':
      yield* itemsNoActions(block, true);
      return;
    case 'add-content':
      yield* addContentItems(store, block);
      return;
    case 'edit-content':
      yield* editContentItems(store, block);
      return;
    case 'reorder-contents':
      yield* reorderContentItems(store);
      return;
    case 'delete-content':
      yield* deleteContentItems(store, block);
      return;
  }
}

@observer
export class X8BlockComponent extends React.Component<X8BlockComponentProps> {
  render() {
    const {mode, block, store} = this.props;
    const {headerContent} = this.props;

    const cardHeaderClassname = cardHeaderClassName(block.BlockType);

    return (
      <div className="card" key={'block' + block.Id}>
        <div className={cardHeaderClassname}>
          <X8BlockCardHeader mode={mode}
                             block={block}
                             store={store}
                             headerContent={headerContent}/>
        </div>
        <div className="card-body">
          {Array.from(renderCardBody(mode, block, store))}
        </div>
      </div>
    );
  }
}
